"use client";

import { useRouter } from "next13-progressbar";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
}

interface AuthProviderProps {
  readonly children: React.ReactNode;
  readonly isLoggedIn: boolean;
  readonly expiresAt?: string | null;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  setIsLoggedIn: () => {},
});

export function AuthProvider({
  children,
  isLoggedIn: initialIsLoggedIn,
  expiresAt,
}: AuthProviderProps) {
  const [isLoggedIn, setIsLoggedIn] = useState(initialIsLoggedIn);
  const router = useRouter();

  useEffect(() => {
    setIsLoggedIn(initialIsLoggedIn);
  }, [initialIsLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && expiresAt) {
      const expirationTime = Number.parseInt(expiresAt, 10);
      const timeUntilExpiry = expirationTime - Date.now();
      if (timeUntilExpiry > 0) {
        const timer = setTimeout(() => {
          setIsLoggedIn(false);
          router.push("/expired");
        }, timeUntilExpiry);

        return () => clearTimeout(timer);
      } else {
        setIsLoggedIn(false);
        router.push("/expired");
      }
    }
  }, [isLoggedIn, expiresAt, router]);

  const contextValue = useMemo(
    () => ({ isLoggedIn, setIsLoggedIn }),
    [isLoggedIn]
  );
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
